import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

function SortableCategoryItem({ cat, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 border-b border-border last:border-0 bg-card"
      data-testid={`category-row-${cat.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary px-1"
        aria-label="Réordonner"
        data-testid={`category-drag-${cat.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span
        className="h-7 w-7 rounded-sm border border-border shrink-0"
        style={{ background: cat.color }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {cat.parent_id && (
            <span className="inline-block mr-1 text-muted-foreground" title="Sous-catégorie">↳</span>
          )}
          {cat.name}
          {cat.parent_id && (
            <span className="ml-2 text-xs text-muted-foreground font-normal">sous-cat.</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground font-mono">{cat.color}</p>
      </div>
      <button
        onClick={() => onEdit(cat)}
        className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-accent"
        data-testid={`category-edit-${cat.id}`}
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => onDelete(cat)}
        className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
        data-testid={`category-delete-${cat.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

export default function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#002FA7", parent_id: "" });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", color: "#002FA7", parent_id: "" });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, color: c.color || "#002FA7", parent_id: c.parent_id || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editing) {
        await api.patch(`/admin/categories/${editing.id}`, payload);
        toast.success("Catégorie modifiée");
      } else {
        await api.post("/admin/categories", payload);
        toast.success("Catégorie créée");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ? Les liens associés deviendront sans catégorie.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((c) => c.id === active.id);
    const newIdx = items.findIndex((c) => c.id === over.id);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    try {
      await api.post("/admin/categories/reorder", { ids: next.map((c) => c.id) });
      toast.success("Ordre des catégories mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// Gestion</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Catégories</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Glissez la poignée <GripVertical className="inline h-3.5 w-3.5" /> pour réordonner les catégories.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
          data-testid="add-category-button"
        >
          <Plus className="h-4 w-4" />
          Nouvelle catégorie
        </button>
      </div>

      <div className="border border-border bg-card rounded-sm">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground" data-testid="categories-empty">
            Aucune catégorie. Créez-en une pour regrouper vos liens.
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <ul className="divide-y divide-border">
                {items.map((c) => (
                  <SortableCategoryItem key={c.id} cat={c} onEdit={openEdit} onDelete={remove} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
            <DialogDescription>Choisissez un nom et une couleur d'accent pour cette catégorie.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nom</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-2"
                data-testid="category-form-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Couleur</Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="h-10 w-16 border border-border rounded-sm bg-background"
                  data-testid="category-form-color"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Catégorie parente (optionnel)</Label>
              <select
                value={form.parent_id}
                onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
                className="mt-2 w-full h-10 px-3 border border-border rounded-sm bg-background text-sm"
                data-testid="category-form-parent"
              >
                <option value="">— Aucune (catégorie racine) —</option>
                {items
                  .filter((c) => !c.parent_id && (!editing || c.id !== editing.id))
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Si renseigné, cette catégorie devient une sous-catégorie (1 seul niveau).
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-10 px-4 rounded-sm border border-border text-sm font-medium hover:bg-accent"
                data-testid="category-form-cancel"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="h-10 px-4 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
                data-testid="category-form-submit"
              >
                {editing ? "Enregistrer" : "Créer"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
